// The one place a 401 is handled. Any query or mutation that fails with 401 (an expired or
// revoked session, including the background `/v1/me` refetch) clears every cached
// authenticated query, so no stale identity or entitlement survives, and sends the user to
// /login with the URL to come back to. Route guards only cover navigations; this covers the rest.
// HARDEN-mfa: the same watch for 403 `mfa_enrollment_required` rereads `/v1/me` instead.
import { ApiError } from "@companyos/sdk";
import type { QueryClient } from "@tanstack/react-query";
import { AuthError, TWO_FACTOR_CODES } from "./client";

export function isUnauthenticated(error: unknown): boolean {
  // SDK operations throw ApiError; the `/v1/auth/*` calls (sessions, password) throw AuthError.
  // HARDEN-mfa: a wrong two-step code is a 401 too, but the session is fine.
  if (error instanceof AuthError && error.code && TWO_FACTOR_CODES.has(error.code)) return false;
  return (error instanceof ApiError || error instanceof AuthError) && error.status === 401;
}

/** HARDEN-mfa: the API refuses this user until they set up two-step sign-in. */
export function isMfaEnrollmentRequired(error: unknown): boolean {
  return error instanceof ApiError && error.code === "mfa_enrollment_required";
}

/**
 * HARDEN-mfa: any query or mutation refused with 403 `mfa_enrollment_required` rereads
 * `/v1/me`, whose `mfa` then makes the shell show the enrolment screen (features/account/mfa.tsx
 * `MfaGate`). The API decides; the session's cached `mfa` may simply be stale (the company's
 * owner switched the requirement on since).
 */
export function watchMfaRequired(queryClient: QueryClient): () => void {
  let pending = false;
  const refetch = () => {
    if (pending) return;
    pending = true;
    queueMicrotask(() => {
      void queryClient.invalidateQueries({ queryKey: ["me"] }).finally(() => {
        pending = false;
      });
    });
  };
  const offQueries = queryClient.getQueryCache().subscribe((event) => {
    if (
      event.type === "updated" &&
      event.action.type === "error" &&
      isMfaEnrollmentRequired(event.action.error)
    )
      refetch();
  });
  const offMutations = queryClient.getMutationCache().subscribe((event) => {
    if (
      event.type === "updated" &&
      event.action.type === "error" &&
      isMfaEnrollmentRequired(event.action.error)
    )
      refetch();
  });
  return () => {
    offQueries();
    offMutations();
  };
}

export function watchSessionExpiry(
  queryClient: QueryClient,
  /** `hadSession`: a signed-in identity was cached, so a session ended (not a first visit). */
  onExpired: (hadSession: boolean) => void,
): () => void {
  let pending = false;
  const expire = () => {
    if (pending) return;
    pending = true;
    const hadSession = queryClient.getQueryData(["me"]) !== undefined;
    // Defer: cache listeners must not mutate the cache synchronously.
    queueMicrotask(() => {
      queryClient.cancelQueries();
      queryClient.removeQueries();
      queryClient.getMutationCache().clear();
      pending = false;
      onExpired(hadSession);
    });
  };
  const offQueries = queryClient.getQueryCache().subscribe((event) => {
    if (
      event.type === "updated" &&
      event.action.type === "error" &&
      isUnauthenticated(event.action.error)
    ) {
      expire();
    }
  });
  const offMutations = queryClient.getMutationCache().subscribe((event) => {
    if (
      event.type === "updated" &&
      event.action.type === "error" &&
      isUnauthenticated(event.action.error)
    ) {
      expire();
    }
  });
  return () => {
    offQueries();
    offMutations();
  };
}
